import { Core } from "@faire/design-tokens";
import { useSettingEnableUnsubscribeByMarketingType } from "@faire/retailer-visitor-shared/settings/useSettingEnableUnsubscribeByMarketingType";
import { useSettingNewEmailUnsubscribePreferencePage } from "@faire/retailer-visitor-shared/settings/useSettingNewEmailUnsubscribePreferencePage";
import { StickySaveProfileButton } from "@faire/retailer-visitor-shared/views/settings/components/StickySaveProfileButton";
import { EmailPreferencesContentWrapper } from "@faire/retailer-visitor-shared/views/settings/email-preferences/components/EmailPreferencesForm";
import { useEmailPreferencesQuery } from "@faire/retailer-visitor-shared/views/settings/email-preferences/hooks/useEmailPreferencesQuery";
import { IEmailPreferenceGroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceGroup";
import { IEmailPreferenceSubgroup } from "@faire/web-api--source/com/faire/email/preferences/IEmailPreferenceSubgroup";
import { IGetEmailPreferencesResponse } from "@faire/web-api--source/com/faire/email/preferences/IGetEmailPreferencesResponse";
import { IUpdateEmailPreferencesRequest } from "@faire/web-api--source/com/faire/email/preferences/IUpdateEmailPreferencesRequest";
import { useMutation as useMutatePreferences } from "@faire/web-api--source/endpoints/www/api/email/preferences/post-hooks";
import { trackRetailerSettingsPageOptOutOfAllFaireMarketingEmailsClick } from "@faire/web-api--source/events/retailerSettingsPage/click/optOutOfAllFaireMarketingEmails";
import { IUnsubscribedMarketingTypeGroup } from "@faire/web-api--source/faire/emailsender/requests/IUnsubscribedMarketingTypeGroup";
import { UnsubscribedMarketingType } from "@faire/web-api--source/indigofair/data/UnsubscribedMarketingType";
import { logError } from "@faire/web/common/logging";
import { isNotUndefined } from "@faire/web/common/typescriptUtils";
import { Button } from "@faire/web/slate/Button";
import { Column, Row } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { Toggle } from "@faire/web/slate/Toggle";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import * as React from "react";
import styled from "styled-components";

type PreferenceUpdatesRecord = Record<string, boolean>;

export const FaireSentEmailPreferences: React.FC = () => {
  const settingNewEmailUnsubscribePreferencePage =
    useSettingNewEmailUnsubscribePreferencePage();
  const settingEnableUnsubscribeByMarketingType =
    useSettingEnableUnsubscribeByMarketingType();

  const [updatedIsUnsubscribed, setUpdatedIsUnsubscribed] =
    React.useState<PreferenceUpdatesRecord>({});
  const [updatedIsUnsubscribedFromAll, setUpdatedIsUnsubscribedFromAll] =
    React.useState<boolean>();

  const { emailPreferencesResponse, updateEmailPreferencesQuery } =
    useEmailPreferencesQuery();
  const {
    mutateAsync: mutatePreferencesAsync,
    isPending: isMutatePreferencesLoading,
  } = useMutatePreferences();

  const initialIsUnsubscribedFromAll =
    emailPreferencesResponse?.unsubscribed_marketing_types.includes(
      UnsubscribedMarketingType.FAIRE_SENT
    ) || !!emailPreferencesResponse?.is_unsubscribed_from_all;
  const isUnsubscribedFromAll =
    updatedIsUnsubscribedFromAll ?? initialIsUnsubscribedFromAll;

  const hasChanged =
    Object.keys(updatedIsUnsubscribed).length > 0 ||
    isUnsubscribedFromAll !== initialIsUnsubscribedFromAll;

  const handleToggleIsUnsubscribed = (
    emailSubgroup: string,
    isUnsubscribed: boolean | undefined
  ) => {
    const newIsUnsubscribedValue = !isUnsubscribed;

    if (newIsUnsubscribedValue === false) {
      setUpdatedIsUnsubscribedFromAll(false);
    }

    setUpdatedIsUnsubscribed((prevState) => {
      const updatedState = { ...prevState };
      if (emailSubgroup in updatedState) {
        delete updatedState[emailSubgroup];
      } else {
        updatedState[emailSubgroup] = newIsUnsubscribedValue;
      }
      return updatedState;
    });
  };

  const handleUnsubscribeFromAll = () => {
    setUpdatedIsUnsubscribed(() => {
      const updatedState: PreferenceUpdatesRecord = {};
      emailPreferencesResponse?.email_preference_groups.forEach((group) => {
        group.subgroups.forEach((subgroup) => {
          if (subgroup.email_subgroup && !subgroup.is_unsubscribed) {
            updatedState[subgroup.email_subgroup] = true;
          }
        });
      });
      return updatedState;
    });
    setUpdatedIsUnsubscribedFromAll(true);
    trackRetailerSettingsPageOptOutOfAllFaireMarketingEmailsClick();
  };

  const submitSubscriptionUpdates = async () => {
    const updatedGroups = emailPreferencesResponse?.email_preference_groups.map(
      (group) => {
        return {
          ...group,
          subgroups: group.subgroups.map((subgroup) => {
            if (
              subgroup.email_subgroup &&
              subgroup.email_subgroup in updatedIsUnsubscribed
            ) {
              return {
                ...subgroup,
                is_unsubscribed: updatedIsUnsubscribed[subgroup.email_subgroup],
              };
            }
            return subgroup;
          }),
        };
      }
    );

    const updateRequest = IUpdateEmailPreferencesRequest.build({
      email_preference_groups: updatedGroups,
    });
    // when we offer brand-level email configurations, we should only opt out of Faire-sent
    if (settingNewEmailUnsubscribePreferencePage) {
      updateRequest.unsubscribed_marketing_type_groups = [
        IUnsubscribedMarketingTypeGroup.build({
          unsubscribed_marketing_type: UnsubscribedMarketingType.FAIRE_SENT,
          is_unsubscribed: isUnsubscribedFromAll,
        }),
      ];
    } else if (settingEnableUnsubscribeByMarketingType) {
      // otherwise, this button unsubscribes from all emails (Faire-sent and brand-sent)
      updateRequest.unsubscribed_marketing_type_groups = [
        IUnsubscribedMarketingTypeGroup.build({
          unsubscribed_marketing_type: UnsubscribedMarketingType.FAIRE_SENT,
          is_unsubscribed: isUnsubscribedFromAll,
        }),
        IUnsubscribedMarketingTypeGroup.build({
          unsubscribed_marketing_type: UnsubscribedMarketingType.BRAND_CRM_SENT,
          is_unsubscribed: isUnsubscribedFromAll,
        }),
      ];
    } else {
      updateRequest.unsubscribe_from_all = isUnsubscribedFromAll;
    }

    const response = await mutatePreferencesAsync([updateRequest]);

    updateEmailPreferencesQuery(
      IGetEmailPreferencesResponse.build({
        ...response,
        unsubscribed_marketing_types:
          response.unsubscribed_marketing_type_groups
            .filter((group) => group.is_unsubscribed)
            .map((group) => group.unsubscribed_marketing_type)
            .filter(isNotUndefined),
      })
    );
    setUpdatedIsUnsubscribed({});
  };

  return (
    <>
      <EmailPreferencesContentWrapper>
        <EmailGroupsList>
          {emailPreferencesResponse?.email_preference_groups.map((group) => (
            <EmailPreferencesGroup
              key={group.title}
              emailPreferenceGroup={group}
              isLoading={isMutatePreferencesLoading}
              handleToggleIsUnsubscribed={handleToggleIsUnsubscribed}
              updatedIsUnsubscribed={updatedIsUnsubscribed}
            />
          ))}
          <LeftAlignedButton
            disabled={isUnsubscribedFromAll || isMutatePreferencesLoading}
            onClick={handleUnsubscribeFromAll}
            variant="tertiary"
          >
            <StrictLocalMsg
              id="FaEHOQ"
              defaultMessage="Opt out of all Faire marketing emails"
            />
          </LeftAlignedButton>
        </EmailGroupsList>
      </EmailPreferencesContentWrapper>
      <StickySaveProfileButton
        onClickPromise={submitSubscriptionUpdates}
        disabled={!hasChanged}
        isVisible={hasChanged}
        data-test-id="save-button"
      >
        <StrictLocalMsg id="jvo0vs" defaultMessage="Save" />
      </StickySaveProfileButton>
    </>
  );
};

interface ISubgroupRequiredProps {
  updatedIsUnsubscribed: PreferenceUpdatesRecord;
  isLoading: boolean;
  handleToggleIsUnsubscribed: (
    emailSubgroup: string,
    isUnsubscribed: boolean | undefined
  ) => void;
}
interface IEmailPreferencesGroupProps extends ISubgroupRequiredProps {
  emailPreferenceGroup: IEmailPreferenceGroup;
}

const EmailPreferencesGroup: React.FC<IEmailPreferencesGroupProps> = ({
  emailPreferenceGroup,
  ...requiredSubgroupProps
}) => {
  const { title, email_group, subgroups } = emailPreferenceGroup;
  return (
    <Column key={email_group}>
      <Typography variant="paragraphSansMedium">{title}</Typography>
      <Spacer size="1x" />
      <GroupColumn>
        {subgroups.map((subgroup) => (
          <EmailPreferencesSubGroup
            key={subgroup.title}
            emailPreferenceSubGroup={subgroup}
            {...requiredSubgroupProps}
          />
        ))}
      </GroupColumn>
    </Column>
  );
};

interface IEmailPreferencesSubGroupProps extends ISubgroupRequiredProps {
  emailPreferenceSubGroup: IEmailPreferenceSubgroup;
}

const EmailPreferencesSubGroup: React.FC<IEmailPreferencesSubGroupProps> = ({
  emailPreferenceSubGroup,
  updatedIsUnsubscribed,
  handleToggleIsUnsubscribed,
  isLoading,
}) => {
  const { title, email_subgroup, description, is_unsubscribed } =
    emailPreferenceSubGroup;

  if (title === undefined || email_subgroup === undefined) {
    logError("all email subgroups must have a title and value");
    return null;
  }

  const isUnsubscribedFromSubgroup =
    updatedIsUnsubscribed[email_subgroup] ?? is_unsubscribed;
  const toggleId = `toggle-${email_subgroup}`;

  return (
    <SubgroupRow key={email_subgroup} justify="space-between">
      <Column>
        <label htmlFor={toggleId}>
          <Typography variant="paragraphSansMedium">{title}</Typography>
          {description ? <Typography>{description}</Typography> : null}
        </label>
      </Column>
      <Spacer size="1x" />
      <Toggle
        id={toggleId}
        hideText
        disabled={isLoading}
        value={!isUnsubscribedFromSubgroup}
        onChange={() => {
          handleToggleIsUnsubscribed(
            email_subgroup,
            isUnsubscribedFromSubgroup
          );
        }}
      />
    </SubgroupRow>
  );
};

const EmailGroupsList = styled.section`
  max-width: 670px;
`;
const GroupColumn = styled(Column)`
  border: 1px solid ${Core.border.muted};
  padding: 8px 24px;
  margin-bottom: 32px;
`;
const SubgroupRow = styled(Row)`
  padding: 16px 0;
  border-top: 1px solid ${Core.border.muted};
  &:first-of-type {
    border-top: unset;
  }
`;
const LeftAlignedButton = styled(Button)`
  text-align: left;
`;
